/** EOD Historical Data (API key): /eod, /real-time, /search. */
import { z } from 'zod';
import { assertDateRange, currencyForSymbol, EpochSeconds, getJson, IsoDateString, parseOrThrow, priceToCents, requireKey, } from './provider.js';
const ID = 'eodhd';
const BASE = 'https://eodhd.com/api';
const N = z.number().nullable();
const EodSchema = z.array(z.object({ date: IsoDateString, open: N, high: N, low: N, close: N, volume: N }));
const RtItem = z.object({ code: z.string(), timestamp: z.union([EpochSeconds, z.string()]), close: z.union([z.number(), z.string()]) });
const RtSchema = z.union([RtItem, z.array(RtItem)]);
const SearchSchema = z.array(z.object({
    Code: z.string(), Exchange: z.string(), Name: z.string(), Type: z.string().nullish(), Currency: z.string().nullish(),
}));
/** EODHD exchange codes → Keel exchange codes ('US' stays 'US': EODHD does not split NYSE/NASDAQ). */
const EXCHANGES = { AU: 'ASX', LSE: 'LSE', NZ: 'NZX', TO: 'TSX', XETRA: 'XETRA' };
const securityType = (t) => {
    switch ((t ?? '').toLowerCase()) {
        case 'common stock': return 'share';
        case 'etf': return 'etf';
        case 'fund': return 'managed_fund';
        case 'bond': return 'bond';
        default: return 'other';
    }
};
export const createEodhdProvider = (ctx) => {
    const url = (path, query = '') => `${BASE}${path}?${query}${query ? '&' : ''}fmt=json&api_token=${encodeURIComponent(requireKey(ctx, ID))}`;
    return {
        id: ID,
        name: 'EOD Historical Data',
        requiresApiKey: true,
        async history(symbol, from, to) {
            assertDateRange(ID, from, to);
            const rows = parseOrThrow(EodSchema, await getJson(ctx, ID, url(`/eod/${encodeURIComponent(symbol)}`, `from=${from}&to=${to}&period=d`)), ID);
            const ccy = currencyForSymbol(ID, symbol) ?? 'USD';
            const c = (v) => (v === null ? null : priceToCents(v, ccy).cents);
            return rows.flatMap((r) => (r.close === null ? [] : [{
                    date: r.date, closeCents: priceToCents(r.close, ccy).cents, open: c(r.open), high: c(r.high), low: c(r.low),
                    volume: r.volume, source: ID,
                }]));
        },
        async quote(symbols) {
            if (symbols.length === 0)
                return [];
            const [first, ...rest] = symbols;
            const body = await getJson(ctx, ID, url(`/real-time/${encodeURIComponent(first)}`, rest.length ? `s=${rest.map(encodeURIComponent).join(',')}` : ''));
            const parsed = parseOrThrow(RtSchema, body, ID);
            return (Array.isArray(parsed) ? parsed : [parsed]).flatMap((r) => {
                if (typeof r.close !== 'number' || typeof r.timestamp !== 'number')
                    return []; // 'NA' when no trade data
                const { cents, currency } = priceToCents(r.close, currencyForSymbol(ID, r.code) ?? 'USD');
                return [{ symbol: r.code, priceCents: cents, currency, asOf: new Date(r.timestamp * 1000).toISOString() }];
            });
        },
        async search(q, opts = {}) {
            const rows = parseOrThrow(SearchSchema, await getJson(ctx, ID, url(`/search/${encodeURIComponent(q)}`, `limit=${opts.limit ?? 10}`)), ID);
            return rows.map((r) => ({
                symbol: `${r.Code}.${r.Exchange}`,
                code: r.Code,
                exchange: EXCHANGES[r.Exchange] ?? r.Exchange,
                name: r.Name,
                type: securityType(r.Type),
                currency: r.Currency ? r.Currency.toUpperCase() : null,
            }));
        },
    };
};
//# sourceMappingURL=eodhd.js.map